import { useState, useMemo, useCallback } from 'react';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_MEDIUM_INBETWEEN } from '~/Constants/UI.constants';
import { LIGHT_GRAY, PRIMARY_BLUE, FITOGRAM_BLUE_DARK_2 } from '~/Constants/Colors.constants';
import { useTranslation } from 'react-i18next';
import { useMerchantValidations } from '../HelperMerchant/useMerchantValidations';
import { IMerchant } from '~/Models/DTO/Merchant.models';
import config from '../HelperMerchant/merchantConfig.json';
import { Route } from '~/Constants/Routes.constants';
import { useNavigation, useProvider, ToastVariant, useToastMessage } from '~/Hooks';
import { requestUpdateMerchant } from '~/requests/providerService/useRequestMerchants';
import { useQueryClient } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isEmpty } from 'lodash-es';

const useStyles = (disabled: boolean) =>
  makeStyles(
    () => ({
      successButton: {
        textTransform: 'capitalize',
        fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
        display: 'flex',
        width: '100%',
        flex: 1,
        borderRadius: 4,
        '&.MuiButton-containedError': {
          background: `${disabled ? LIGHT_GRAY : PRIMARY_BLUE}`,
          '&:hover': {
            background: `${disabled ? LIGHT_GRAY : FITOGRAM_BLUE_DARK_2}`,
          },
        },
      },
    }),
    { name: 'BackButton' },
  );
interface IUseMerchantModalActions {
  setOpen: (open: boolean) => void;
  merchant: IMerchant;
  myDivRef: any;
}
export const useMerchantModalActions = ({
  setOpen,
  merchant,
  myDivRef,
}: IUseMerchantModalActions) => {
  const [tab, setTab] = useState(0);
  const { showToast } = useToastMessage();
  const [isLoading, setIsLoading] = useState(false);
  const { navigate } = useNavigation();
  const { currentProvider } = useProvider();
  const queryClient = useQueryClient();
  const { asyncValidate, errors, values } = useMerchantValidations(merchant, true);
  const nextDisable = useMemo(
    () =>
      !!config?.fields
        ?.filter(({ groupBy }) => groupBy === tab)
        ?.find(({ name }) => !!errors?.[name]) ||
      (tab == 2 && !!errors?.consent),
    [tab, errors],
  );

  const { classes } = useStyles(nextDisable)();
  const { t } = useTranslation();
  const closeAction = useCallback(() => {
    setOpen(false);
    setTab(0);
  }, [tab]);

  const confirm = useMemo(
    () => ({
      title: t(tab == 2 ? 'app.submit' : 'app.next'),
      action: () => {
        if (tab === 2) {
          setTab(0);
          onSave();
          return;
        }
        setTab(tab + 1);
      },
      disabled: !!nextDisable || isLoading,
      classes: { root: classes.successButton },
      fullWidth: tab == 0,
    }),
    [tab, nextDisable, isLoading],
  );

  const cancel = useMemo(
    () => ({
      title: t('app.back'),
      /*eslint func-names: ["error", "never"]*/
      action() {
        const reason = arguments?.[1] || '';
        const forbiddenActs = ['backdropClick', 'escapeKeyDown'];
        if (forbiddenActs.includes(reason) || tab === 0) {
          return;
        }
        setTab(tab - 1);
      },
      hidden: tab < 1,
    }),
    [tab],
  );

  const onSave = async () => {
    setIsLoading(true);
    try {
      await requestUpdateMerchant({ ...values, venueId: currentProvider?.organisationVenueId });
      navigate(Route.ORGANISATION_MERCHANT, { domain: currentProvider?.domain || '' });
      setIsLoading(false);
      setOpen(false);
      await queryClient?.invalidateQueries({ queryKey: [REQUEST_ACTIONS.GET_MERCHANT_LIST] });
    } catch (e: any) {
      const message = e?.detail;
      if (!isEmpty(e)) {
        showToast(
          message || JSON.stringify(e),
          message || JSON.stringify(e),
          ToastVariant.ERROR,
          true,
        );
      }
      setIsLoading(false);
      if (!!myDivRef?.current) {
        myDivRef.current.scrollTop = myDivRef?.current?.scrollHeight;
      }
    }
  };

  return {
    tab,
    setTab,
    confirm,
    cancel,
    closeAction,
    validation: asyncValidate,
    onSave,
    isLoading,
  };
};
